import { useEffect, useRef, useState, type ReactNode } from 'react'
import { AlertCircle } from 'lucide-react'
import { errorDetail, type EnrolmentClient } from './totpClient'

/**
 * Authenticator-app enrolment: issue a pending secret, show it as a QR code
 * and as a key to type, and enforce nothing until a code from the app is
 * confirmed. Used in two places - Settings > Security (a signed-in person) and
 * the sign-in page (a person the platform's two-factor policy has diverted
 * through enrolment, holding an enrolment token that reaches only these three
 * endpoints) - so it talks to the server through a small injected client.
 *
 * The secret lives in this component's state and nowhere else: not in storage,
 * not in a URL, not in a log. It is dropped when the component goes away.
 */

/** `JBSWY3DP…` as `JBSW Y3DP …` - read off a screen and typed by hand. */
function groupKey(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim()
}

type Phase = 'intro' | 'starting' | 'scan'

type Props = {
  client: EnrolmentClient
  onDone: (result: unknown) => void
  onCancel?: () => void
  /** Present only when re-keying an enrolled account. */
  currentCode?: string
  /** Issue the secret on mount (once) rather than on a click. */
  autoBegin?: boolean
  /** When given, a refused start is reported here instead of being shown inline
   *  (the caller can send the person back to re-enter their current code). */
  onStartFailed?: (message: string) => void
  intro?: ReactNode
  beginLabel?: string
  buttonClassName?: string
  inputClassName?: string
}

export default function TotpEnrolmentFlow({
  client,
  onDone,
  onCancel,
  currentCode,
  autoBegin = false,
  onStartFailed,
  intro,
  beginLabel = 'Begin set-up',
  buttonClassName = 'btn-primary text-[12px] disabled:opacity-50',
  inputClassName = 'w-full rounded border border-amber-400/15 bg-black/40 px-2 py-1.5 text-center text-[14px] tracking-[0.4em] text-stone-100 outline-none focus:border-amber-400/40',
}: Props) {
  const [phase, setPhase] = useState<Phase>('intro')
  const [secret, setSecret] = useState('')
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [qrFailed, setQrFailed] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Issuing a secret replaces any earlier pending one, so it must happen once
  // per start: a double-invoked effect or a double click must not issue two.
  const started = useRef(false)

  useEffect(() => {
    return () => {
      // Only a blob URL needs releasing; an inline image is just a string.
      if (qrUrl?.startsWith('blob:')) URL.revokeObjectURL(qrUrl)
    }
  }, [qrUrl])

  const begin = async () => {
    if (started.current) return
    started.current = true
    setPhase('starting')
    setError(null)
    try {
      const issued = await client.setup(currentCode)
      setSecret(issued.secret)
      if (issued.qr_png_base64) {
        // The server sends the image with the secret. A replacement for an
        // enrolled account can be fetched no other way, by design.
        setQrUrl(`data:image/png;base64,${issued.qr_png_base64}`)
        setQrFailed(false)
      } else {
        try {
          const blob = await client.qrcode()
          setQrUrl(URL.createObjectURL(blob))
          setQrFailed(false)
        } catch {
          setQrUrl(null)
          setQrFailed(true)
        }
      }
      setPhase('scan')
    } catch (err) {
      const message = errorDetail(err, 'Could not start set-up.')
      started.current = false
      setPhase('intro')
      if (onStartFailed) onStartFailed(message)
      else setError(message)
    }
  }

  useEffect(() => {
    if (autoBegin) void begin()
    // `begin` is guarded by `started`; running it once on mount is the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await client.confirm(code)
      setSecret('')
      setQrUrl(null)
      onDone(result)
    } catch (err) {
      setError(errorDetail(err, 'Invalid code. Try again.'))
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      {phase !== 'scan' ? (
        <>
          {intro ? <div className="text-[11px] leading-relaxed text-stone-400">{intro}</div> : null}
          {phase === 'starting' ? (
            <div className="text-[11px] text-stone-500">Preparing your authenticator set-up…</div>
          ) : (
            <div className="flex items-center gap-2">
              <button type="button" className={buttonClassName} onClick={() => void begin()}>
                {beginLabel}
              </button>
              {onCancel ? (
                <button type="button" className="text-[11px] text-stone-500 hover:text-stone-300" onClick={onCancel}>
                  Cancel
                </button>
              ) : null}
            </div>
          )}
        </>
      ) : (
        <div className="space-y-3">
          <p className="text-[11px] leading-relaxed text-stone-400">
            1. In your authenticator app (Microsoft Authenticator, Google Authenticator, 1Password and the
            like), add an account and scan this code.
          </p>
          {qrUrl ? (
            <img
              alt="Authenticator QR code"
              src={qrUrl}
              className="mx-auto h-40 w-40 rounded bg-white p-1.5"
            />
          ) : null}
          {qrFailed ? (
            <p className="text-[11px] text-amber-300">
              The QR image could not be loaded. Enter the key below in your app instead.
            </p>
          ) : null}
          <div className="text-[11px] text-stone-500">
            Cannot scan? Enter this key by hand:
            <div className="mt-1 break-all rounded border border-amber-400/10 bg-black/40 px-2 py-1.5 text-center font-mono text-[12px] tracking-wider text-stone-200">
              {groupKey(secret)}
            </div>
          </div>
          <p className="text-[11px] leading-relaxed text-stone-400">
            2. Enter the six-digit code the app now shows. Nothing is switched on until you do.
          </p>
          <input
            aria-label="Authenticator code"
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            className={inputClassName}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={(event) => event.key === 'Enter' && code.length === 6 && !busy && void confirm()}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={buttonClassName}
              disabled={busy || code.length !== 6}
              onClick={() => void confirm()}
            >
              {busy ? 'Checking…' : 'Confirm and turn on'}
            </button>
            {onCancel ? (
              <button type="button" className="text-[11px] text-stone-500 hover:text-stone-300" onClick={onCancel}>
                Cancel
              </button>
            ) : null}
          </div>
        </div>
      )}
      {error ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
          <AlertCircle size={13} className="mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      ) : null}
    </div>
  )
}
